import { useState, useEffect } from 'react';
import { FlowNode, FlowOption, FlowCondition } from '../types';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface NodeEditPanelProps {
  node: FlowNode;
  onUpdateNode: (node: FlowNode) => void;
  onClose: () => void;
}

const NodeEditPanel = ({ node, onUpdateNode, onClose }: NodeEditPanelProps) => {
  const [editedNode, setEditedNode] = useState<FlowNode>({ ...node });

  useEffect(() => {
    setEditedNode({ ...node });
  }, [node]);

  const handleSave = () => {
    onUpdateNode(editedNode);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleAddOption = () => {
    setEditedNode({
      ...editedNode,
      options: [
        ...editedNode.options,
        { label: 'Nova Opção', value: `opt_${Date.now()}` }
      ]
    });
  };

  const handleUpdateOption = (index: number, option: FlowOption) => {
    const newOptions = [...editedNode.options];
    newOptions[index] = option;
    setEditedNode({ ...editedNode, options: newOptions });
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = editedNode.options.filter((_, i) => i !== index);
    setEditedNode({ ...editedNode, options: newOptions });
  };

  const handleAddCondition = () => {
    setEditedNode({
      ...editedNode,
      conditions: [
        ...editedNode.conditions,
        { variable: 'nova_variavel', operator: 'equals', value: 'valor' }
      ]
    });
  };

  const handleUpdateCondition = (index: number, condition: FlowCondition) => {
    const newConditions = [...editedNode.conditions];
    newConditions[index] = condition;
    setEditedNode({ ...editedNode, conditions: newConditions });
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = editedNode.conditions.filter((_, i) => i !== index);
    setEditedNode({ ...editedNode, conditions: newConditions });
  };

  return (
    <div className="w-96 bg-white border-l border-gray-200 flex flex-col max-h-screen">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Editar Nó {editedNode.type === 'message' ? '💬' :
                     editedNode.type === 'question' ? '❓' :
                     editedNode.type === 'input' ? '✏️' :
                     editedNode.type === 'condition' ? '🔀' :
                     editedNode.type === 'end' ? '🏁' : '⚪'}
        </h3>
        
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título do Nó
            </label>
            <input
              type="text"
              value={editedNode.title || ''}
              onChange={(e) => setEditedNode({ ...editedNode, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Título para identificação"
            />
          </div>

          {/* Type Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo do Nó
            </label>
            <select
              value={editedNode.type}
              onChange={(e) => setEditedNode({ ...editedNode, type: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="message">💬 Mensagem</option>
              <option value="question">❓ Pergunta</option>
              <option value="input">✏️ Entrada</option>
              <option value="condition">🔀 Condição</option>
              <option value="end">🏁 Fim</option>
            </select>
          </div>
        </div>

        {/* Text Content */}
        {(editedNode.type === 'message' || editedNode.type === 'question' || editedNode.type === 'input') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {editedNode.type === 'message' ? 'Texto da Mensagem' :
               editedNode.type === 'question' ? 'Texto da Pergunta' :
               'Instrução do Input'}
            </label>
            <textarea
              value={editedNode.text || ''}
              onChange={(e) => setEditedNode({ ...editedNode, text: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder={
                editedNode.type === 'message' ? 'Digite a mensagem que será enviada...' :
                editedNode.type === 'question' ? 'Digite a pergunta que será feita...' :
                'Digite a instrução para o usuário...'
              }
            />
          </div>
        )}

        {/* Variable Name */}
        {editedNode.type === 'input' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome da Variável
            </label>
            <input
              type="text"
              value={editedNode.variable || ''}
              onChange={(e) => setEditedNode({ ...editedNode, variable: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="nome_da_variavel"
            />
            <p className="mt-1 text-xs text-gray-500">
              A resposta do usuário será armazenada nesta variável
            </p>
          </div>
        )}

        {/* Options */}
        {editedNode.type === 'question' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Opções de Resposta
              </label>
              <button
                onClick={handleAddOption}
                className="inline-flex items-center px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                <PlusIcon className="w-3 h-3 mr-1" />
                Adicionar
              </button>
            </div>
            
            <div className="space-y-3">
              {editedNode.options.map((option, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Opção {index + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveOption(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Texto da Opção
                      </label>
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => handleUpdateOption(index, { ...option, label: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Ex: Sim, quero informações"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Valor (interno)
                      </label>
                      <input
                        type="text"
                        value={option.value}
                        onChange={(e) => handleUpdateOption(index, { ...option, value: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Ex: sim"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Próximo Nó (ID)
                      </label>
                      <input
                        type="text"
                        value={option.next_node || ''}
                        onChange={(e) => handleUpdateOption(index, { ...option, next_node: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        placeholder="ID do próximo nó"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {editedNode.options.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-300 rounded">
                  Nenhuma opção adicionada. Clique em "Adicionar" para criar uma opção.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Conditions */}        
        {editedNode.type === 'condition' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Condições
              </label>
              <button
                onClick={handleAddCondition}
                className="inline-flex items-center px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                <PlusIcon className="w-3 h-3 mr-1" />
                Adicionar
              </button>
            </div>
            
            <div className="space-y-3">
              {editedNode.conditions.map((condition, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Condição {index + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveCondition(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Variável
                      </label>
                      <input
                        type="text"
                        value={condition.variable}
                        onChange={(e) => handleUpdateCondition(index, { ...condition, variable: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        placeholder="nome_da_variavel"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Operador
                      </label>
                      <select
                        value={condition.operator}
                        onChange={(e) => handleUpdateCondition(index, { ...condition, operator: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="equals">É igual a</option>
                        <option value="contains">Contém</option>
                        <option value="not_empty">Não é vazio</option>
                        <option value="is_empty">É vazio</option>
                        <option value="greater_than">Maior que</option>
                        <option value="less_than">Menor que</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Valor
                      </label>
                      <input
                        type="text"
                        value={condition.value}
                        onChange={(e) => handleUpdateCondition(index, { ...condition, value: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        placeholder="valor_esperado"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Próximo Nó (se verdadeiro)
                      </label>
                      <input
                        type="text"
                        value={condition.next_node || ''}
                        onChange={(e) => handleUpdateCondition(index, { ...condition, next_node: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        placeholder="ID do próximo nó"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {editedNode.conditions.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-300 rounded">
                  Nenhuma condição adicionada. Clique em "Adicionar" para criar uma condição.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Next Node */}
        {editedNode.type !== 'question' && editedNode.type !== 'condition' && editedNode.type !== 'end' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Próximo Nó (ID)
            </label>
            <input
              type="text"
              value={editedNode.next_node || ''}
              onChange={(e) => setEditedNode({ ...editedNode, next_node: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="ID do próximo nó"
            />
            <p className="mt-1 text-xs text-gray-500">
              Deixe vazio se este for o último nó ou conecte através do editor visual
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 flex space-x-2">
        <button
          onClick={handleSave}
          className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          Salvar
        </button>
        
        <button
          onClick={handleCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default NodeEditPanel;